import React, { useState, useEffect } from 'react';

export const SignUp = ({ onBack }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [pulseIndex, setPulseIndex] = useState(0);

  // Live pulse messages - rotates through these
  const pulseMessages = [
    { location: 'California', action: 'shared their lupus journey', time: '2 min ago' },
    { location: 'Texas', action: 'joined the community', time: '5 min ago' },
    { location: 'New York', action: 'shared their story', time: '8 min ago' },
    { location: 'Florida', action: 'helped a research study', time: '12 min ago' },
    { location: 'Illinois', action: 'shared their experience', time: '15 min ago' },
  ];

  // Rotate pulse messages
  useEffect(() => {
    const interval = setInterval(() => {
      setPulseIndex((prev) => (prev + 1) % pulseMessages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [pulseMessages.length]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (agreedToTerms) {
      setSubmitted(true);
    }
  };

  // Password strength calculation
  const getPasswordStrength = (pwd) => {
    if (!pwd) return { level: 0, label: '', color: '#e5e5e5' };
    let score = 0;
    if (pwd.length >= 8) score++;
    if (pwd.length >= 12) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd)) score++;
    if (/[^A-Za-z0-9]/.test(pwd)) score++;

    if (score <= 1) return { level: 1, label: 'Weak', color: '#E8B4B8' };
    if (score <= 2) return { level: 2, label: 'Fair', color: '#EBA09B' };
    if (score <= 3) return { level: 3, label: 'Good', color: '#F5B9A5' };
    if (score <= 4) return { level: 4, label: 'Strong', color: '#B5C9A8' };
    return { level: 5, label: 'Very Strong', color: '#4A7C59' };
  };

  const passwordStrength = getPasswordStrength(password);

  const inputStyle = {
    width: '100%',
    padding: '14px 16px',
    fontSize: '15px',
    border: '1.5px solid #e0ddd9',
    borderRadius: '8px',
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    boxSizing: 'border-box',
    backgroundColor: '#fff',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  };

  const labelStyle = {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: '#272726',
    marginBottom: '8px'
  };

  const handleInputFocus = (e) => {
    e.target.style.borderColor = '#EBA09B';
    e.target.style.boxShadow = '0 0 0 3px rgba(235, 160, 155, 0.15)';
  };

  const handleInputBlur = (e) => {
    e.target.style.borderColor = '#e0ddd9';
    e.target.style.boxShadow = 'none';
  };

  // Wave decoration component
  const WaveDecoration = () => (
    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '200px', overflow: 'hidden', zIndex: 0 }}>
      <svg
        viewBox="0 0 1440 200"
        preserveAspectRatio="none"
        style={{ width: '100%', height: '100%' }}
      >
        <defs>
          <linearGradient id="signupWaveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#EBA09B" stopOpacity="0.3" />
            <stop offset="30%" stopColor="#F5B9A5" stopOpacity="0.25" />
            <stop offset="60%" stopColor="#F5D7AA" stopOpacity="0.2" />
            <stop offset="100%" stopColor="#B5C9A8" stopOpacity="0.15" />
          </linearGradient>
        </defs>
        <path
          d="M0,100 C300,150 600,50 900,100 C1200,150 1350,80 1440,120 L1440,0 L0,0 Z"
          fill="url(#signupWaveGradient)"
        />
        <path
          d="M0,80 C200,120 500,40 800,90 C1100,140 1300,60 1440,100 L1440,0 L0,0 Z"
          fill="url(#signupWaveGradient)"
          opacity="0.5"
        />
      </svg>
    </div>
  );

  // Logo component matching header
  const Logo = () => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '32px' }}>
      <span
        style={{
          fontFamily: '"Source Serif Pro", Georgia, serif',
          fontSize: '28px',
          fontWeight: '700',
          color: '#272726'
        }}
      >
        Real
      </span>
      <span style={{ position: 'relative', display: 'inline-block' }}>
        <span
          style={{
            position: 'absolute',
            left: '-4%',
            right: '-20%',
            bottom: '0',
            height: '50%',
            background: 'linear-gradient(90deg, rgba(235, 160, 155, 0.85) 0%, rgba(245, 185, 165, 0.8) 40%, rgba(250, 200, 160, 0.75) 70%, rgba(245, 215, 170, 0.6) 100%)',
            transform: 'skewX(-2deg) rotate(-1deg)',
            zIndex: -1,
            borderRadius: '2px 8px 6px 2px',
            clipPath: 'polygon(0% 20%, 5% 0%, 15% 25%, 25% 5%, 40% 20%, 55% 0%, 70% 15%, 85% 5%, 95% 20%, 100% 10%, 100% 85%, 95% 100%, 80% 90%, 65% 100%, 50% 85%, 35% 100%, 20% 90%, 5% 100%, 0% 85%)'
          }}
        />
        <span
          style={{
            fontFamily: '"Source Serif Pro", Georgia, serif',
            fontSize: '28px',
            fontWeight: '700',
            color: '#272726'
          }}
        >
          Patients
        </span>
      </span>
    </div>
  );

  // Live Community Pulse
  const LivePulse = () => {
    const currentMessage = pulseMessages[pulseIndex];

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          padding: '16px 24px',
          backgroundColor: 'rgba(181, 201, 168, 0.15)',
          borderRadius: '100px',
          maxWidth: 'fit-content',
          margin: '0 auto'
        }}
      >
        <style>{`
          @keyframes blink {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.4; }
          }
          @keyframes fadeSlide {
            0% { opacity: 0; transform: translateY(10px); }
            10% { opacity: 1; transform: translateY(0); }
            90% { opacity: 1; transform: translateY(0); }
            100% { opacity: 0; transform: translateY(-10px); }
          }
        `}</style>
        <div
          style={{
            width: '8px',
            height: '8px',
            backgroundColor: '#4A7C59',
            borderRadius: '50%',
            animation: 'blink 2s ease-in-out infinite'
          }}
        />
        <span
          key={pulseIndex}
          style={{
            fontSize: '14px',
            color: '#272726',
            animation: 'fadeSlide 4s ease-in-out'
          }}
        >
          A patient in <strong>{currentMessage.location}</strong> {currentMessage.action}
          <span style={{ color: '#888', marginLeft: '8px' }}>{currentMessage.time}</span>
        </span>
      </div>
    );
  };

  if (submitted) {
    return (
      <div
        style={{
          minHeight: '100vh',
          backgroundColor: '#f7f5f3',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 20px',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          position: 'relative'
        }}
      >
        <WaveDecoration />
        <div
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '16px',
            padding: '48px 40px',
            maxWidth: '440px',
            width: '100%',
            boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
            textAlign: 'center',
            position: 'relative',
            zIndex: 1
          }}
        >
          <div
            style={{
              width: '72px',
              height: '72px',
              background: 'linear-gradient(135deg, #EBA09B 0%, #F5B9A5 50%, #F5D7AA 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px'
            }}
          >
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>
          <h1
            style={{
              fontFamily: '"Source Serif Pro", Georgia, serif',
              fontSize: '28px',
              fontWeight: '700',
              color: '#272726',
              margin: '0 0 12px 0'
            }}
          >
            Welcome aboard!
          </h1>
          <p
            style={{
              fontSize: '16px',
              color: '#666',
              margin: '0 0 28px 0',
              lineHeight: '1.6'
            }}
          >
            Thank you for joining Real Patients. Your voice matters, and together we're making healthcare better for everyone.
          </p>
          <button
            onClick={onBack}
            style={{
              padding: '14px 32px',
              fontSize: '15px',
              fontWeight: '600',
              color: '#272726',
              backgroundColor: 'transparent',
              border: '2px solid #272726',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#272726';
              e.target.style.color = '#ffffff';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = '#272726';
            }}
          >
            Continue Exploring
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: '#f7f5f3',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        position: 'relative',
        overflowX: 'hidden'
      }}
    >
      {/* Mobile responsive styles */}
      <style>{`
        @media (max-width: 480px) {
          .signup-card {
            padding: 32px 24px !important;
          }
          .signup-name-fields {
            flex-direction: column !important;
            gap: 16px !important;
          }
        }
      `}</style>
      <WaveDecoration />

      {/* Main content wrapper */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '160px 20px 80px',
          position: 'relative',
          zIndex: 1
        }}
      >

      {/* Back button */}
      {onBack && (
        <button
          onClick={onBack}
          style={{
            position: 'fixed',
            top: '24px',
            left: '24px',
            zIndex: 1000,
            padding: '10px 18px',
            backgroundColor: '#fff',
            border: '1.5px solid #e0ddd9',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '500',
            fontSize: '14px',
            color: '#272726',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.target.style.borderColor = '#272726';
          }}
          onMouseLeave={(e) => {
            e.target.style.borderColor = '#e0ddd9';
          }}
        >
          ← Back
        </button>
      )}

      {/* Main card */}
      <div
        className="signup-card"
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '16px',
          padding: '48px 40px',
          maxWidth: '420px',
          width: '100%',
          boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
          position: 'relative',
          zIndex: 1
        }}
      >
        <Logo />

        <h1
          style={{
            fontFamily: '"Source Serif Pro", Georgia, serif',
            fontSize: '24px',
            fontWeight: '700',
            color: '#272726',
            margin: '0 0 8px 0',
            textAlign: 'center'
          }}
        >
          Join 60,000+ patients
        </h1>
        <p
          style={{
            fontSize: '15px',
            color: '#666',
            margin: '0 0 28px 0',
            textAlign: 'center',
            lineHeight: '1.5'
          }}
        >
          Share your story and help shape the future of healthcare
        </p>

        {/* Google button only */}
        <button
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            width: '100%',
            padding: '14px 20px',
            backgroundColor: '#ffffff',
            border: '1.5px solid #e0ddd9',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '15px',
            fontWeight: '500',
            color: '#272726',
            transition: 'border-color 0.2s',
            marginBottom: '20px'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#272726';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#e0ddd9';
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </button>

        {/* Divider */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            marginBottom: '20px'
          }}
        >
          <div style={{ flex: 1, height: '1px', backgroundColor: '#e0ddd9' }} />
          <span style={{ fontSize: '13px', color: '#999', fontWeight: '500' }}>or</span>
          <div style={{ flex: 1, height: '1px', backgroundColor: '#e0ddd9' }} />
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Name fields */}
          <div className="signup-name-fields" style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>First name</label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                style={inputStyle}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Last name</label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                style={inputStyle}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
              />
            </div>
          </div>

          {/* Email */}
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="your@email.com"
              style={inputStyle}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
            />
          </div>

          {/* Password */}
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Create a password"
              style={inputStyle}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
            />

            {/* Password strength indicator */}
            {password && (
              <div style={{ marginTop: '10px' }}>
                <div style={{ display: 'flex', gap: '4px', marginBottom: '6px' }}>
                  {[1, 2, 3, 4, 5].map((level) => (
                    <div
                      key={level}
                      style={{
                        flex: 1,
                        height: '3px',
                        borderRadius: '2px',
                        backgroundColor: level <= passwordStrength.level ? passwordStrength.color : '#e5e5e5',
                        transition: 'background-color 0.2s'
                      }}
                    />
                  ))}
                </div>
                <div style={{ fontSize: '12px', color: passwordStrength.color, fontWeight: '500' }}>
                  {passwordStrength.label}
                </div>
              </div>
            )}
          </div>

          {/* Terms checkbox */}
          <div style={{ marginBottom: '24px' }}>
            <label
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                cursor: 'pointer'
              }}
            >
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                style={{
                  width: '18px',
                  height: '18px',
                  marginTop: '2px',
                  accentColor: '#EBA09B',
                  cursor: 'pointer'
                }}
              />
              <span style={{ fontSize: '13px', color: '#666', lineHeight: '1.5' }}>
                I agree to the{' '}
                <span style={{ color: '#272726', fontWeight: '500', cursor: 'pointer' }}>Terms of Service</span>
                {' '}and{' '}
                <span style={{ color: '#272726', fontWeight: '500', cursor: 'pointer' }}>Privacy Policy</span>
              </span>
            </label>
          </div>

          {/* Submit button */}
          <button
            type="submit"
            disabled={!agreedToTerms}
            style={{
              width: '100%',
              padding: '14px 24px',
              fontSize: '16px',
              fontWeight: '600',
              color: '#ffffff',
              background: agreedToTerms
                ? 'linear-gradient(135deg, #EBA09B 0%, #F5B9A5 50%, #F5D7AA 100%)'
                : '#ccc',
              border: 'none',
              borderRadius: '8px',
              cursor: agreedToTerms ? 'pointer' : 'not-allowed',
              transition: 'transform 0.2s, box-shadow 0.2s'
            }}
            onMouseEnter={(e) => {
              if (agreedToTerms) {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 4px 12px rgba(235, 160, 155, 0.4)';
              }
            }}
            onMouseLeave={(e) => {
              if (agreedToTerms) {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }
            }}
          >
            Create Account
          </button>
        </form>

        {/* Sign in link */}
        <p
          style={{
            fontSize: '14px',
            color: '#666',
            margin: '20px 0 0 0',
            textAlign: 'center'
          }}
        >
          Already have an account?{' '}
          <span style={{ color: '#272726', fontWeight: '600', cursor: 'pointer' }}>
            Sign in
          </span>
        </p>
      </div>

      {/* Live Community Pulse - below the card */}
      <div style={{ marginTop: '40px' }}>
        <LivePulse />
      </div>
      </div>
    </div>
  );
};
